import styled, { keyframes } from 'styled-components';
import { rem } from 'polished';

const fadeIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(10px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

const bounce = keyframes`
  0%, 60%, 100% {
    transform: translateY(0);
  }
  30% {
    transform: translateY(-4px);
  }
`;

export const PhoneFrame = styled.div`
  width: ${rem('320px')};
  height: ${rem('580px')};
  background: linear-gradient(145deg, #2d2d2d 0%, #1a1a1a 50%, #0d0d0d 100%);
  border-radius: ${rem('40px')};
  padding: ${rem('8px')};
  box-shadow:
    0 25px 50px -12px rgba(0, 0, 0, 0.5),
    0 0 0 ${rem('2px')} #3a3a3a,
    inset 0 0 ${rem('3px')} rgba(255, 255, 255, 0.1);
  position: relative;
  border: ${rem('3px')} solid #2a2a2a;

  &::before {
    content: '';
    position: absolute;
    top: ${rem('8px')};
    left: 50%;
    transform: translateX(-50%);
    width: ${rem('90px')};
    height: ${rem('22px')};
    background: linear-gradient(180deg, #1a1a1a 0%, #0d0d0d 100%);
    border-radius: 0 0 ${rem('14px')} ${rem('14px')};
    z-index: 10;
  }

  @media (max-width: 768px) {
    width: ${rem('280px')};
    height: ${rem('500px')};
    border-radius: ${rem('32px')};
  }
`;

export const ChatContainer = styled.div`
  width: 100%;
  height: 100%;
  background: #e5ddd5;
  border-radius: ${rem('30px')};
  overflow: hidden;
  display: flex;
  flex-direction: column;
  background-image: url("data:image/svg+xml,%3Csvg width='60' height='60' viewBox='0 0 60 60' xmlns='http://www.w3.org/2000/svg'%3E%3Cg fill='none' fill-rule='evenodd'%3E%3Cg fill='%23c9c2b8' fill-opacity='0.4'%3E%3Cpath d='M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E");
`;

export const ChatHeader = styled.div`
  background: #075e54;
  padding: ${rem('35px')} ${rem('16px')} ${rem('12px')};
  display: flex;
  align-items: center;
  gap: ${rem('12px')};
  color: white;
`;

export const HeaderAvatar = styled.div`
  width: ${rem('40px')};
  height: ${rem('40px')};
  border-radius: 50%;
  background: #128c7e;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
  }
`;

export const HeaderInfo = styled.div`
  flex: 1;
`;

export const HeaderName = styled.div`
  font-weight: 600;
  font-size: ${rem('16px')};
`;

export const HeaderStatus = styled.div`
  font-size: ${rem('12px')};
  opacity: 0.8;
`;

export const HeaderIcons = styled.div`
  display: flex;
  gap: ${rem('16px')};

  svg {
    opacity: 0.9;
  }
`;

export const MessagesContainer = styled.div`
  flex: 1;
  padding: ${rem('16px')};
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: ${rem('8px')};

  &::-webkit-scrollbar {
    width: 6px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(0, 0, 0, 0.2);
    border-radius: 3px;
  }
`;

export const MessageBubble = styled.div`
  max-width: 85%;
  padding: ${rem('8px')} ${rem('12px')};
  border-radius: ${rem('8px')};
  position: relative;
  animation: ${fadeIn} 0.3s ease-out;

  ${({ $isUser }) =>
    $isUser
      ? `
    align-self: flex-end;
    background: #dcf8c6;
    border-top-right-radius: 0;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      right: -8px;
      border-width: 0 0 10px 10px;
      border-style: solid;
      border-color: transparent transparent transparent #dcf8c6;
    }
  `
      : `
    align-self: flex-start;
    background: white;
    border-top-left-radius: 0;

    &::after {
      content: '';
      position: absolute;
      top: 0;
      left: -8px;
      border-width: 0 10px 10px 0;
      border-style: solid;
      border-color: transparent white transparent transparent;
    }
  `}
`;

export const MessageText = styled.p`
  margin: 0;
  font-size: ${rem('14px')};
  line-height: 1.4;
  color: #303030;
  word-wrap: break-word;
`;

export const MessageTime = styled.span`
  display: flex;
  align-items: center;
  justify-content: flex-end;
  gap: ${rem('4px')};
  font-size: ${rem('11px')};
  color: #667781;
  margin-top: ${rem('4px')};
`;

export const MessageStatus = styled.span`
  color: #53bdeb;
  font-size: ${rem('14px')};
`;

export const TypingIndicator = styled.div`
  display: flex;
  gap: ${rem('4px')};
  padding: ${rem('4px')} 0;
`;

export const TypingDot = styled.span`
  width: ${rem('8px')};
  height: ${rem('8px')};
  background: #667781;
  border-radius: 50%;
  animation: ${bounce} 1s infinite;
  animation-delay: ${({ $delay }) => $delay || '0s'};
`;

export const InputContainer = styled.div`
  background: #f0f0f0;
  padding: ${rem('8px')} ${rem('12px')};
  display: flex;
  align-items: center;
  gap: ${rem('8px')};
`;

export const InputField = styled.input`
  flex: 1;
  background: white;
  border: none;
  border-radius: ${rem('20px')};
  padding: ${rem('10px')} ${rem('16px')};
  font-size: ${rem('14px')};
  outline: none;

  &::placeholder {
    color: #667781;
  }
`;

export const InputIcons = styled.div`
  display: flex;
  gap: ${rem('8px')};
  font-size: ${rem('20px')};
  color: #54656f;
`;
